import { useEffect } from 'react';

import { useOnceWhen } from '../../../hooks';
import {
  identifyMarketplaceUser,
  trackDashboardLanded,
  trackDashboardReturned,
} from '../../../services/analytics/marketplaceEvents';
import BootedDashboard from '../dashboardStates/BootedDashboard';
import { DealWithFarthestState, UserDeal } from '../utils';
import MarketplaceFaq from './marketplace/faq/MarketplaceFaq';
import MarketplaceLiveDashboard from './marketplace/live/MarketplaceLiveDashboard';
import MarketplacePrelistDashboard from './marketplace/prelist/MarketplacePrelistDashboard';
import MarketplacePrelistLoadingSkeleton from './marketplace/prelist/MarketplacePrelistLoadingSkeleton';
import useMarketplaceDashboardState from './marketplace/useMarketplaceDashboardState';

type MarketplaceDashboardProps = {
  userDeal: UserDeal;
  isDealBooted: boolean;
};

const MarketplaceDashboard = ({ userDeal, isDealBooted }: MarketplaceDashboardProps) => {
  const dealId = userDeal?.id || '';
  const { lead, marketplaceState, isLoading, itemsComplete, error } =
    useMarketplaceDashboardState(dealId);

  const vin = userDeal?.car?.vin;
  const { listingStatus } = marketplaceState;
  const hasLead = lead != null;
  const offerAccepted = lead?.acceptedAt != null;

  const showBootedFallback = isDealBooted && error == null && listingStatus === 'building';

  useEffect(() => {
    if (!vin || !hasLead || showBootedFallback) return;
    identifyMarketplaceUser({ vin, leadStatus: listingStatus, offerAccepted });
  }, [vin, hasLead, listingStatus, offerAccepted, showBootedFallback]);

  useOnceWhen(error == null && !isLoading && itemsComplete != null && !showBootedFallback, () => {
    if (itemsComplete == null) return;
    const anyStarted =
      marketplaceState.conditionReport.status !== 'notStarted' ||
      marketplaceState.photos.status !== 'notStarted';
    if (anyStarted) {
      trackDashboardReturned({ dealId, listingStatus, itemsComplete });
    } else {
      trackDashboardLanded({ dealId, listingStatus, itemsComplete });
    }
  });

  if (showBootedFallback) {
    return isLoading ? (
      <MarketplacePrelistLoadingSkeleton />
    ) : (
      <BootedDashboard deal={userDeal as DealWithFarthestState} />
    );
  }

  return (
    <>
      {listingStatus === 'building' ? (
        <MarketplacePrelistDashboard userDeal={userDeal} />
      ) : (
        <MarketplaceLiveDashboard userDeal={userDeal} />
      )}
      <MarketplaceFaq userDeal={userDeal} lead={lead} listingStatus={listingStatus} />
    </>
  );
};

export default MarketplaceDashboard;
